import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './store/authStore';
import Layout from './components/Layout';
import LoginPage from './pages/Login';
import DashboardPage from './pages/Dashboard';
import PropertiesPage from './pages/Properties';
import PeoplePage from './pages/People';
import LeasesPage from './pages/Leases';
import ChargesPage from './pages/Charges';
import FinancePage from './pages/Finance';
import MapDashboard from './pages/MapDashboard';
import ContractTemplatesPage from './pages/ContractTemplates';
import EmployeesPage from './pages/Employees';
import DepartmentsPage from './pages/Departments';
import PayrollPage from './pages/Payroll';
import OnboardingCompliancePage from './pages/OnboardingCompliance';
import PropertyCapturePage from './pages/PropertyCapture';
import CrmPage from './pages/Crm';
import BankingPage from './pages/Banking';
import BankConfPage from './pages/BankConf';
import InboxPage from './pages/Inbox';
import ListingsPage from './pages/Listings';
import CaixaPage from './pages/Caixa';
import ReceiptTemplatesPage from './pages/ReceiptTemplates';
import ContasPage from './pages/Contas';
import PainelFinanceiro from './pages/PainelFinanceiro';
import SettingsPage from './pages/Settings';
import PromissoryPage from './pages/Promissory';
import PortalLayout from './portal/PortalLayout';
import PortalHome from './portal/PortalHome';
import PortalListings from './portal/PortalListings';
import PortalProperty from './portal/PortalProperty';
import { QuemSomos, Servicos, Contato, PoliticaPrivacidade, PoliticaCookies, TermosUso } from './portal/PortalPages';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        {/* Portal público (sem login) */}
        <Route element={<PortalLayout />}>
          <Route path="/" element={<PortalHome />} />
          <Route path="/imoveis" element={<PortalListings />} />
          <Route path="/imovel/:id" element={<PortalProperty />} />
          <Route path="/quem-somos" element={<QuemSomos />} />
          <Route path="/servicos" element={<Servicos />} />
          <Route path="/contato" element={<Contato />} />
          <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
          <Route path="/politica-de-cookies" element={<PoliticaCookies />} />
          <Route path="/termos-de-uso" element={<TermosUso />} />
        </Route>

        <Route path="/login" element={<LoginPage />} />

        {/* Área administrativa (protegida) */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/map" element={<MapDashboard />} />
                  <Route path="/properties" element={<PropertiesPage />} />
                  <Route path="/people" element={<PeoplePage />} />
                  <Route path="/leases" element={<LeasesPage />} />
                  <Route path="/contract-templates" element={<ContractTemplatesPage />} />
                  <Route path="/charges" element={<ChargesPage />} />
                  <Route path="/finance" element={<FinancePage />} />
                  <Route path="/employees" element={<EmployeesPage />} />
                  <Route path="/departments" element={<DepartmentsPage />} />
                  <Route path="/payroll" element={<PayrollPage />} />
                  <Route path="/onboarding-compliance" element={<OnboardingCompliancePage />} />
                  <Route path="/property-capture" element={<PropertyCapturePage />} />
                  <Route path="/inbox" element={<InboxPage />} />
                  <Route path="/listings" element={<ListingsPage />} />
                  <Route path="/caixa" element={<CaixaPage />} />
                  <Route path="/contas" element={<ContasPage />} />
                  <Route path="/promissory" element={<PromissoryPage />} />
                  <Route path="/painel-financeiro" element={<PainelFinanceiro />} />
                  <Route path="/receipt-templates" element={<ReceiptTemplatesPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/crm" element={<CrmPage />} />
                  <Route path="/banking" element={<BankingPage />} />
                  <Route path="/bankconf" element={<BankConfPage />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </QueryClientProvider>
  );
}

export default App;
